/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {useEffect, useState} from "react";
import {Package} from "types/entities/Package";
import {RouteComponentProps, withRouter} from "react-router-dom";
import {useTypedSelector} from "helpers/reducers";
import {ArticleType} from "types/enums/ArticleType";
import {translate, uniqueFilter} from "utils/utility";
import {ServerImage} from "components/ServerImage";
import {isWebpSupported} from "react-image-webp/dist/utils";
import {PersonType} from "types/enums/PersonType";
import DriverCount from "pages/Front/Booking/components/GiftCardPackage/DriverCount";
import {useDispatch} from "useDispatch";
import {ThunkAction} from "redux-thunk";
import ShopAction from "store/shop/ShopAction";
import {DEFAULT_HALL_ID} from "helpers/config";
import ArticlesAction from "store/articles/ArticlesAction";
import {articlePrice, filterArticlesByType, useShopItemsCartHook} from "utils/giftCardShopUtils";
import {Article} from "types/entities/Article";
import GiftCardShoppingCart from "pages/Front/Booking/components/GiftCardPackage/GiftCardShoppingCart";
import Loader from "components/Loader";
import {ActivityType} from "types/enums/ActivityType";

interface Params {
    package_id: string;
}

interface PackageDetailProps extends RouteComponentProps<Params> {
    packageDetail: Package;
}

const GiftCardPackageDetail = (props: PackageDetailProps) => {
    const dispatch = useDispatch();
    const itemsCart = useTypedSelector(state => state.shopState.itemsCart);
    const session = useTypedSelector(state => state.sessionState.session);
    const articles = useTypedSelector(state => state.articlesState);
    const {addToCart} = useShopItemsCartHook();
    const [adultArticlesCount, setAdultArticlesCount] = useState(1);
    const [childArticlesCount, setChildArticlesCount] = useState(1);
    const [giftArticles, setGiftArticles] = useState<Article[]>([]);
    const mainImageUrl = isWebpSupported() ? props.packageDetail.main_image_url.replace("jpg", "webp") : props.packageDetail.main_image_url;
    const packageActivities = props.packageDetail.packageItems.map(packageItem => packageItem.activity_id);
    const uniquePackageActivities: number = uniqueFilter(packageActivities);
    const packageActivity = uniquePackageActivities[0] === 1 ? ActivityType.Karting : ActivityType.TheParkPlayground;
    const packageLimit = packageActivity === ActivityType.Karting ? 15 : 10;
    const countChanged = e => {
    };

    const createShopArticleItemsCart = () => {
        dispatch<ThunkAction<any, any, any, any>>(ShopAction.fetchCreateShopArticleItemsCart(DEFAULT_HALL_ID)).then(r => {
            dispatch(ShopAction.resetShoppingCart());
            dispatch(ShopAction.setShopArticleItemsCart(r.shopArticleItemsCart));
        });
    };

    const giftArticlesHandler = () => {
        const allArticles = filterArticlesByType(articles.all as Article[], ArticleType.Online, uniquePackageActivities[0]);
        setGiftArticles(allArticles);
    };

    const countChangeHandler = (e, personType: PersonType, increment) => {
        switch (personType) {
            case PersonType.Adult:
                if (adultArticlesCount + increment <= packageLimit && adultArticlesCount + increment >= 0) {
                    return setAdultArticlesCount(prev => prev + increment);
                }
                return null;
            case PersonType.Child:
                if (childArticlesCount + increment <= packageLimit && childArticlesCount + increment >= 0) {
                    return setChildArticlesCount(prev => prev + increment);
                }
                return null;
            default:
                return null;
        }
    };

    useEffect(() => {
        if (
            session &&
            session.access_token
            // && !itemsCart.shopArticleItemsCart
        ) {
            createShopArticleItemsCart();
        }
    }, [session]);

    useEffect(() => {
        if (!itemsCart) {
            return;
        }
    }, [adultArticlesCount, childArticlesCount]);

    useEffect(() => {
        if (!articles.all || articles.all.length == 0) {
            dispatch(ArticlesAction.fetchArticlesWithHall());
        }
    }, []);

    useEffect(() => {
        if (articles.all) {
            giftArticlesHandler();
        }
    }, [articles.all]);

    if (!giftArticles) {
        return <Loader />;
    }

    return (
        <div key={props.packageDetail.id} className="GiftCardPackageDetail flex flex-col md:flex-row gap-4">
            <div
                className={"GiftCardPackageDetailInfo z-1 bg-white rounded-md shadow-md overflow-hidden flex flex-col text-base"}>
                <div
                    className={
                        "GiftCardPackageDetailInfoTitle flex flex-col sm:flex-row justify-center items-center border-b border-gray-600 items-stretch self-stretch"
                    }
                >
                    <div
                        className={"GiftCardPackageDetailInfoImage w-full sm:w-1/2 xl:w-2/5 flex flex-col flex-grow relative"}>
                        <div className={"flex flex-row flex-grow aspect-square"}>
                            <ServerImage
                                alt={"Package Detail"}
                                className={"aspect-[2/1] sm:aspect-[3/2] xl:aspect-[4/3] w-full h-full object-cover"}
                                src={mainImageUrl}
                            />
                        </div>
                    </div>
                    <main
                        className={"GiftCardPackageDetailActivities px-4 sm:px-8 w-full sm:w-1/2 xl:w-2/3 py-4 xl:py-8 flex flex-col"}>
                        <h3 className={"text-xl sm:text-2xl font-bold mb-2 sm:mb-4 xl:mb-8 text-primary-800"}>{translate(props.packageDetail.name)}</h3>
                        <h4 className={"sm:text-base text-sm font-medium text-gray-800 whitespace-pre-line"}>{translate(props.packageDetail.description)}</h4>
                    </main>
                </div>
                <div
                    className={
                        "GiftCardPackageDetailOffer p-4 sm:p-6 xl:p-8 flex flex-col lg:flex-row space-y-4 lg:space-y-0 lg:space-x-4 xl:space-x-8 justify-between"
                    }
                >
                    <DriverCount
                        activity={packageActivity}
                        count={adultArticlesCount}
                        countChangeHandler={countChangeHandler}
                        countChanged={countChanged}
                        personType={PersonType.Adult}
                        price={articlePrice(adultArticlesCount, PersonType.Adult, giftArticles)}
                        onClick={() => addToCart(adultArticlesCount, PersonType.Adult, giftArticles)}
                    />
                    <DriverCount
                        activity={packageActivity}
                        count={childArticlesCount}
                        countChangeHandler={countChangeHandler}
                        countChanged={countChanged}
                        personType={PersonType.Child}
                        price={articlePrice(childArticlesCount, PersonType.Child, giftArticles)}
                        onClick={() => addToCart(childArticlesCount, PersonType.Child, giftArticles)}
                    />
                </div>
            </div>
            <GiftCardShoppingCart withCheckoutButton={true} />
        </div>
    );
};

export default withRouter(GiftCardPackageDetail);
